import Phaser from 'phaser';
import { PALETTE } from '../../config';
import { InputController } from '../../game/InputController';
import { SaveManager } from '../../game/SaveManager';
import { effectiveStats, FATIGUE_PER_RACE, boostOpts, getBoost, type BoostId } from '../../game/Economy';
import { submitScore, fetchLeaderboard } from '../../game/api';
import { arenaResult } from '../../game/arena';
import { getArenaPlay, clearArenaPlay } from '../../game/arenaContext';
import { Ads } from '../../game/ads';
import { tracking } from '../../game/tracker';
import { findAthlete } from '../../game/data/athletes';
import { DISCIPLINES } from '../../game/data/disciplines';
import type { Athlete, DisciplineResult, LeaderboardRecord } from '../../game/types';
import { viewport } from '../../ui/viewport';

export interface DisciplineInit {
  athleteId: string;
  /** One-race boost selected (and paid for) in RosterSelect, applied to the effective stats. */
  boostId?: BoostId;
}

/** Distance-metric events (everything else is timed) — for the abandon (DNF) result. */
const DISTANCE_IDS = new Set(['long_jump', 'javelin', 'high_jump', 'triple_jump', 'shot_put', 'hammer_throw', 'pole_vault', 'discus', 'weightlifting', 'diving', 'archery']);

/**
 * Base for every discipline scene. Resolves the chosen athlete, owns the semantic
 * InputController, adds a quit (abandon) button, and standardises the hand-off to Results.
 * Subclasses implement gameplay in their own create()/update() (calling super first).
 */
export abstract class DisciplineScene extends Phaser.Scene {
  protected controls!: InputController;
  protected athlete!: Athlete;
  private boostId?: BoostId;
  private quitting = false;

  init(data: DisciplineInit): void {
    this.quitting = false;
    this.boostId = data.boostId;
    const athlete = findAthlete([...SaveManager.instance.roster], data.athleteId);
    if (!athlete) {
      // Defensive: bad nav data shouldn't crash — bounce to menu.
      this.scene.start('MainMenu');
      return;
    }
    // Race with a clone whose stats already fold in training bonuses, the current fatigue
    // penalty and any one-race boost, so every discipline benefits with no per-scene changes.
    // The persisted athlete keeps its clean base stats; only fatigue is mutated (commitAndShow).
    this.athlete = { ...athlete, stats: effectiveStats(athlete, boostOpts(data.boostId)) };
  }

  create(): void {
    // CrazyGames: mark the start of ACTUAL gameplay (no ads will run during a race), and stop it
    // whenever we leave the scene (to Results / menu), covering every exit path in one place.
    Ads.gameplayStart();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => Ads.gameplayStop());
    const cfg = Object.values(DISCIPLINES).find((d) => d.sceneKey === this.sys.settings.key);
    tracking.raceStart({ discipline: cfg?.id ?? this.sys.settings.key });
    this.controls = new InputController(this);
    this.addQuitButton();
    if (this.boostId) this.showBoostBadge(this.boostId);
  }

  /** Small "boost active" chip so the player knows a consumable is in effect this race. */
  private showBoostBadge(boostId: BoostId): void {
    const boost = getBoost(boostId);
    if (!boost) return;
    const { s } = viewport(this);
    const t = this.add.text(16 * s, 14 * s, `${boost.emoji} ${boost.name}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(13, Math.round(15 * s))}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: '#6ef0a0', padding: { x: 8, y: 4 },
    }).setOrigin(0, 0).setScrollFactor(0).setDepth(200);
    this.tweens.add({ targets: t, alpha: { from: 1, to: 0.7 }, duration: 700, yoyo: true, repeat: 2, delay: 1200, onComplete: () => t.setAlpha(0.85) });
  }

  /** Top-right "abandon race" button, present in every discipline. */
  private addQuitButton(): void {
    const { w, s } = viewport(this);
    const r = Math.max(20, 22 * s);
    const x = w - (26 * s + r);
    const y = 26 * s + r;
    const c = this.add.circle(x, y, r, PALETTE.bad, 0.85).setStrokeStyle(2, 0xffffff, 0.4).setScrollFactor(0).setDepth(200);
    const t = this.add.text(x, y, '🏳', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(r * 1.1)}px` }).setOrigin(0.5).setScrollFactor(0).setDepth(201);
    [c, t].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => this.abandon()));
  }

  /** Fetch the current world record (global #1) for a discipline, then run `cb` with it. */
  protected fetchWorldRecord(disciplineId: string, kind: 'time' | 'distance', cb: (metric: number) => void): void {
    void fetchLeaderboard(disciplineId, kind, 1).then((rows) => {
      const m = rows && rows[0] ? rows[0].metric : 0;
      if (m > 0) cb(m);
    });
  }

  /** Gold dashed "world record" marker on the field at world x = markerX, with a flag label. */
  protected drawGroundRecord(markerX: number, groundY: number, topY: number, label: string): void {
    const g = this.add.graphics().setDepth(3);
    for (let yy = topY; yy < groundY; yy += 14) g.fillStyle(PALETTE.sun, 0.95).fillRect(markerX - 2, yy, 4, 8);
    this.add.text(markerX, topY - 4, `🌍 ${label}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '13px', color: '#0b1020', fontStyle: 'bold', backgroundColor: '#ffd24a', padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(4);
  }

  /** Abandon the race: hand off to Results as a DNF (no score/record submitted). */
  protected abandon(): void {
    if (this.quitting) return;
    this.quitting = true;
    const cfg = Object.values(DISCIPLINES).find((d) => d.sceneKey === this.sys.settings.key);
    const id = cfg?.id ?? 'sprint_100m';
    this.finishDiscipline({
      disciplineId: id,
      metricKind: DISTANCE_IDS.has(id) ? 'distance' : 'time',
      athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality,
      metric: 0, score: 0, reward: { coins: 0, medal: 'none' }, rank: 99,
      standings: [{ name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: 0 }],
      dnf: true,
    });
  }

  /** Persist rewards, record the leaderboard entry, and show the results readout. */
  protected finishDiscipline(result: DisciplineResult): void {
    this.controls.setEnabled(false);
    // Abandoned race: straight to Results as DNF — no coins, no leaderboard record, no tournament.
    if (result.dnf) {
      clearArenaPlay();
      this.scene.start('Results', result);
      return;
    }
    // "NUOVO RECORD" only when the WORLD record (global #1) for this discipline is beaten —
    // not for winning vs the AI, nor a personal best. Check the board BEFORE submitting.
    let done = false;
    const proceed = (): void => { if (done) return; done = true; this.commitAndShow(result); };
    void fetchLeaderboard(result.disciplineId, result.metricKind, 1)
      .then((rows) => {
        const wr = rows && rows[0] ? rows[0].metric : null;
        result.newRecord = wr == null
          ? result.metric > 0 // empty board: this result IS the first/world record
          : result.metricKind === 'time' ? result.metric < wr : result.metric > wr;
        proceed();
      })
      .catch(() => proceed());
    this.time.delayedCall(1500, proceed); // fallback if the network is slow/offline
  }

  /** Apply rewards, record the result + meta boards, and open Results. */
  private commitAndShow(result: DisciplineResult): void {
    const save = SaveManager.instance;
    save.addCoins(result.reward.coins);
    if (result.reward.medal !== 'none') save.addMedal(result.reward.medal);
    save.updateAthlete(result.athleteId, (a) => {
      a.fatigue = Math.min(100, a.fatigue + FATIGUE_PER_RACE);
    });
    const playerName = save.teamName || 'Team Anonimo';
    const identity = {
      athleteId: result.athleteId, athleteName: result.athleteName,
      species: result.species, nationality: result.nationality, playerName, clientId: save.clientId,
    };
    const record: LeaderboardRecord = {
      ...identity,
      disciplineId: result.disciplineId,
      metric: result.metric,
      metricKind: result.metricKind,
      score: result.score,
      rank: result.rank,
      createdAt: Date.now(),
    };
    save.addRecord(record);
    tracking.score(result.metric, { discipline: result.disciplineId, rank: result.rank, kind: result.metricKind });
    if (result.newRecord) tracking.achievement(`record_${result.disciplineId}`, { discipline: result.disciplineId });
    if (result.rank === 1 || result.newRecord) Ads.happytime(); // a win/record is a joy moment
    void submitScore(record); // best-effort global leaderboard — also feeds the derived medal table
    void submitScore({ ...identity, disciplineId: 'coins', metricKind: 'distance', metric: save.coins, score: 0, rank: 1, createdAt: Date.now() });
    // If this race was played FOR a tournament, submit the result to the Arena and tag the
    // result so Results offers a "back to tournament" button. Consumed once, then cleared.
    const play = getArenaPlay();
    clearArenaPlay();
    if (play && play.disciplineId === result.disciplineId && result.metric > 0) {
      result.arena = { tournamentId: play.tournamentId, tournamentName: play.tournamentName };
      void arenaResult(play.tournamentId, result.disciplineId, result.metric);
    }
    // A world record gets the full celebration scene first; it hands off to Results itself.
    this.scene.start(result.newRecord ? 'RecordCelebration' : 'Results', result);
  }
}
